import {useNavigate, useParams} from "react-router-dom";
import {useForm} from "react-hook-form";
import React, {useState, useEffect, useContext, useRef, forwardRef} from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input, Divider,
  Textarea, Alert, AlertIcon, AlertTitle,
  Container,
  InputProps,
  ChakraComponent,
} from '@chakra-ui/react';
import ReactPaginate from "react-paginate";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {client} from "@/client"
import {GlobalContext, bearerToken, emptyUser} from "@/auth";
import {Log} from "@/pbgen/v1/main_pb"
import {
  Page,
  PageHeader,
  PageBody,
} from "@/components/layout"

const getTimeZoneInfo = (): string => {
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = { timeZoneName: "short" };
  const formatter = new Intl.DateTimeFormat(undefined, options);
  const parts = formatter.formatToParts(date);
  const timeZoneName = parts.find(part => part.type === "timeZoneName")?.value;

  return timeZoneName || "Unknown Time Zone";
}

const getTimeZoneOffset = (): string => {
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = { timeZoneName: 'longOffset' };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);
  const timeZoneOffset = parts.find(part => part.type === 'timeZoneName')?.value;

  return timeZoneOffset || 'Unknown Time Zone';
}

const toLocalISOString = (date: Date): string => {
  const pad = (num: number): string => (num < 10 ? '0' : '') + num;

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '- ';
  let offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);

  // return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMinutes}`;
  return `${year}${month}${day}${hours}${minutes}${seconds}`;
}

export const Logs = () => {
  const {globalState, setGlobalState} = useContext(GlobalContext);
  const navigate = useNavigate();
  const [logs, setLogs] = useState<Log[]>([]);

   // ファイルダウンロード処理
   const handleDownload = async () => {
    try {
      const resp = await client.getAllUserEx({
        bearerToken: bearerToken()
      });

      // Blob に変換
      var blob = new Blob([resp.buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);

      // ダウンロード用のリンクを作成
      const a = document.createElement("a");
      a.href = url;
      a.download = "AllUsers.xlsx"; // ダウンロードファイル名
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error:', error);
    }
  };


  const getCsv = () => {
    const csv = encodeURI("data:text/csv;charset=utf-8,\ufeff" +
      [[
        "\"日時\"",
        "\"アクション\"",
        "\"ユーザーID\"",
        "\"ユーザー名\"",
        "\"ステータス\"",
        "\"事業者ID\"",
        "\"事業者名\"",
      ]].concat(
        logs.map((log) => {
          const {id, userId} = log
          return [
            `"${log.createdAtRfc3339}"`,
            `"${log.action}"`,
            `"${log.userId}"`,
            `"${log.userName}"`,
            `"${log.userStatus}"`,
            `"${log.vendorId}"`,
            `"${log.vendorName}"`,
          ]
        })
      ).map(e => e.join(",")).join("\n")
    )
    return csv
  }

  const [fileName, setFileName] = useState("logs.csv");

  const downloadCsv = () => {
    const csvUrl = getCsv();
    const link = document.createElement('a');
    link.href = csvUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const [since, setSince] = useState<Date | null>(null);
  const [until, setUntil] = useState<Date | null>(null)

  useEffect(() => {(async () => {
    const resp = await client.getLogs({
      bearerToken: bearerToken(),
      optSinceRfc3339: since?.toISOString(),
      optUntilRfc3339: until?.toISOString(),
      optTimezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    setLogs(resp.logs);
  })();}, [since, until]) // eslint-disable-line react-hooks/exhaustive-deps

  const timeZoneName = getTimeZoneInfo();
  const timeOffset = getTimeZoneOffset();

  return <Page>
    <PageHeader title={"ログ"}>
      <HStack padding={2}>
        <Button onClick={ () => handleDownload() }>全ユーザー出力</Button>
        <Button isDisabled={window.history.length <= 2} onClick={() => {navigate(-1)}}>戻る</Button>
      </HStack>
    </PageHeader>
    <PageBody>
      <VStack spacing={6}>
        <FormControl><FormLabel>開始日時（{timeOffset}）</FormLabel><DatePicker
          selected={since}
          onChange={(since: Date) => {
            setSince(since);
            setFileName(`logs-${since ? toLocalISOString(since).replaceAll(/[-\+:\.:\+]/g, "_") : 'null'}-${until ? toLocalISOString(until).replaceAll(/[-\+:\.:\+]/g, "_") : 'null'}.csv`);
          }}
          startDate={since}
          endDate={until}
          placeholderText="null"
          customInput={React.createElement(forwardRef<HTMLInputElement, InputProps>((props, ref) => (<Input ref={ref} {...props}/>)))}
          showTimeSelect
          dateFormat="yyyy-MM-dd HH:mm:ss"
          popperPlacement="right"
          timeFormat="HH:mm"
          strictParsing={true}
        /></FormControl>
        <FormControl><FormLabel>終了日時（{timeOffset}）</FormLabel><DatePicker
          selected={until}
          onChange={(until: Date) => {
            setUntil(until);
            setFileName(`logs-${since ? toLocalISOString(since).replaceAll(/[-\+:\.:\+]/g, "_") : 'null'}-${until ? toLocalISOString(until).replaceAll(/[-\+:\.:\+]/g, "_") : 'null'}.csv`);
          }}
          selectsEnd
          startDate={since}
          endDate={until}
          minDate={since}
          placeholderText="null"
          customInput={React.createElement(forwardRef<HTMLInputElement, InputProps>((props, ref) => (<Input ref={ref} {...props}/>)))}
          showTimeSelect
          dateFormat="yyyy-MM-dd HH:mm:ss"
          popperPlacement="right"
          timeFormat="HH:mm"
          strictParsing={true}
        /></FormControl>
        <HStack width="100%">
        <FormControl><FormLabel>ファイル名</FormLabel><Input 
          placeholder="Enter file name" 
          value={fileName} 
          onChange={(e) => setFileName(e.target.value)} 
        /></FormControl>
        <Button onClick={downloadCsv}>ダウンロード</Button>
        </HStack>
        <Table variant="striped">
          <Thead>
            <Tr>
              <Th>日時</Th>
              <Th>アクション</Th>
              <Th>ユーザーID</Th>
              <Th>ユーザー名</Th>
              <Th>ステータス</Th>
              <Th>事業者ID</Th>
              <Th>事業者名</Th>
            </Tr>
          </Thead>
          <Tbody>
            {logs.map((log, idx) => {
              return <Tr key={log.id.toString()}>
                <Td>{log.createdAtRfc3339}</Td>
                <Td>{log.action}</Td>
                <Td>{log.userId}</Td>
                <Td>{log.userName}</Td>
                <Td>{log.userStatus}</Td>
                <Td>{log.vendorId}</Td>
                <Td>{log.vendorName}</Td>
              </Tr>
            })}
          </Tbody>
        </Table>
      </VStack>
    </PageBody>
  </Page>
}
